import {useCallback, useState} from 'react'
import {Button, SelectPanel, SkeletonBox} from '@primer/react'
import {ChevronRightIcon, CommentDiscussionIcon, GitBranchIcon, TriangleDownIcon, XIcon} from '@primer/octicons-react'
import {ReposSelector} from '@github-ui/repos-selector'
import {RefSelector} from '@github-ui/ref-selector'
import {scopedRepositoryQuery} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {queryForRepos} from '../task-chat-input-toolbar/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'

import {isRepoInScope} from '../shared'
import {ChatTargetPicker} from './ChatTargetPicker'
import type {ChatOwnerControl} from './chat-owner-control'
import styles from './RepoBranchPill.module.css'

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface RepoBranchPair {
  id: string
  repo: AgentDropdownRepository
  branch: string
}

// ─── REPO BRANCH PILL ───────────────────────────────────────────────────────

export function RepoBranchPill({
  pair,
  onRepoChange,
  onBranchChange,
  onRemove,
  removable,
  hideRepo,
  initialRepoNWO,
  onLoad,
  secondaryAction,
  disabled,
  scopeOwner,
  repoPickerSubtitle,
  allowChat = false,
  onChatSelect,
  chatOwner,
}: {
  pair: RepoBranchPair
  onRepoChange: (pairId: string, repo: AgentDropdownRepository) => void
  onBranchChange: (pairId: string, branch: string) => void
  onRemove: (pairId: string) => void
  removable: boolean
  hideRepo?: boolean
  initialRepoNWO?: string
  onLoad?: (repositories: AgentDropdownRepository[]) => void
  disabled?: boolean
  /** Strict owner filter; null blocks loading while the scoped owner resolves. */
  scopeOwner?: string | null
  /** Subtitle rendered under the repo picker title. */
  repoPickerSubtitle?: string
  allowChat?: boolean
  onChatSelect?: () => void
  chatOwner?: ChatOwnerControl
  secondaryAction?:
    | React.ReactElement<typeof SelectPanel.SecondaryActionButton>
    | React.ReactElement<typeof SelectPanel.SecondaryActionLink>
}) {
  const repoFullName = pair.repo.nameWithOwner
  // While searching, hide the "Chat" select-all option and the owner subtitle so
  // the list reads as a flat repo list. The search value only reaches this
  // component through `repositoryLoader`, so we track it there.
  const [isSearching, setIsSearching] = useState(false)

  const repositoryLoader = useCallback(
    async (query: string, defaultOwner?: string, rejectOnHttpError = false) => {
      if (scopeOwner === null || scopeOwner === '') return []
      setIsSearching(query.trim().length > 0)
      const effectiveOwner = scopeOwner ?? defaultOwner
      const scopedQuery = scopedRepositoryQuery(query, scopeOwner)
      const repos = rejectOnHttpError
        ? await queryForRepos(scopedQuery, effectiveOwner, initialRepoNWO, {rejectOnHttpError: true})
        : await queryForRepos(scopedQuery, effectiveOwner, initialRepoNWO)
      if (!scopeOwner) return repos
      return repos.filter(repo => isRepoInScope(repo, scopeOwner))
    },
    [initialRepoNWO, scopeOwner],
  )
  const strictRepositoryLoader = useCallback(
    (query: string) => repositoryLoader(query, undefined, true),
    [repositoryLoader],
  )

  return (
    <span className={styles.pill}>
      {!hideRepo && (
        <>
          {chatOwner && allowChat ? (
            <ChatTargetPicker
              chatOwner={chatOwner}
              selectedRepository={pair.repo}
              chatSelected={false}
              triggerText={pair.repo.nameOnly}
              triggerLabel={`Repository: ${repoFullName}`}
              triggerClassName={styles.pillButton}
              disabled={disabled}
              repositoryLoader={strictRepositoryLoader}
              includedRepositoryNameWithOwner={initialRepoNWO}
              onLoad={onLoad}
              onSelectRepository={repo => {
                if (isRepoInScope(repo, scopeOwner)) onRepoChange(pair.id, repo)
              }}
              onSelectChat={() => onChatSelect?.()}
              secondaryAction={secondaryAction}
              scopeOwner={scopeOwner}
              loadingElement={<SkeletonBox width="80px" height="24px" />}
            />
          ) : (
            <ReposSelector<AgentDropdownRepository>
              repositoryLoader={repositoryLoader}
              selectionVariant="single"
              iconOnly={false}
              responsiveStyle="full"
              selectAllOption={allowChat && !isSearching}
              selectAllOptionPlacement="inline"
              selectAllOptionText="Chat"
              selectAllOptionLeadingVisual={CommentDiscussionIcon}
              buttonText={pair.repo.nameOnly}
              onSelect={repo => {
                if (repo) {
                  if (isRepoInScope(repo, scopeOwner)) onRepoChange(pair.id, repo)
                } else if (allowChat) onChatSelect?.()
              }}
              currentSelection={pair.repo}
              width="medium"
              visibleRowCount={6}
              additionalButtonProps={{
                'aria-label': `Repository: ${repoFullName}`,
                variant: 'invisible',
                size: 'small',
                className: styles.pillButton,
                disabled,
              }}
              onLoad={onLoad}
              loadingElement={<SkeletonBox width="80px" height="24px" />}
              secondaryAction={secondaryAction}
              subtitle={isSearching ? undefined : repoPickerSubtitle}
              restrictionOwner={scopeOwner ?? undefined}
            />
          )}
          <ChevronRightIcon size={12} className={styles.pillChevron} />
        </>
      )}
      <RefSelector
        cacheKey={`env-strip-branch-${pair.id}`}
        canCreate={false}
        currentCommitish={pair.branch}
        defaultBranch={pair.repo.defaultBranch}
        owner={pair.repo.owner}
        repo={pair.repo.nameOnly}
        types={['branch']}
        selectedRefType="branch"
        closeOnSelect
        hideShowAll
        actionListHeadingText="Select a branch"
        onSelectItem={(branch: string) => onBranchChange(pair.id, branch)}
        disabled={disabled}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderAnchor={(anchorProps: any) => (
          <Button
            {...anchorProps}
            aria-label={`Branch: ${pair.branch}`}
            variant="invisible"
            size="small"
            className={`${styles.pillButton} ${hideRepo ? styles.branchOnlyButton : ''}`}
            leadingVisual={hideRepo ? GitBranchIcon : undefined}
            trailingAction={hideRepo ? TriangleDownIcon : undefined}
          >
            {pair.branch}
          </Button>
        )}
      />
      {removable && (
        <button
          type="button"
          aria-label={`Remove ${repoFullName}`}
          onClick={() => onRemove(pair.id)}
          className={styles.pillRemove}
          disabled={disabled}
        >
          <XIcon size={12} />
        </button>
      )}
    </span>
  )
}
